import { createSelector } from 'reselect';

import { REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS } from '../../../constants/organizationsKeys';
import { IShipmentInfoGate } from '../../../types/createSendShipmentInfo';
import { isUnknownState, isValidZip } from '../../../utils/addressValidation';
import { isStateRequired } from '../../../utils/country';
import { isValidEmail } from '../../../utils/createSendRecipients';
import { IAppState } from '../../reducers';
import { selectCurrentOrganizationOptions } from '../../selectors/organizations';
import { selectCsIsBusinessReasonRequiredByCampaign } from './contents';
import { selectIsCsOrgSettingsResolved } from './orgSettings';
import { selectCsBusinessReasonId, selectIsCsReleaseDateMissing } from './sendSettings';

export const selectCsShipmentInfoState = (state: IAppState) => state.createSend.shipmentInfo;

export const selectCsShipmentInfoForm = createSelector(selectCsShipmentInfoState, (shipmentInfo) => shipmentInfo.form);

export const selectCsShipmentInfoSelectedAddressId = createSelector(
  selectCsShipmentInfoState,
  (shipmentInfo) => shipmentInfo.selectedAddressId,
);

export const selectCsShipmentInfoAddressApplied = createSelector(
  selectCsShipmentInfoState,
  (shipmentInfo) => shipmentInfo.addressApplied,
);

export const selectIsShipmentBusinessReasonRequiredByOrg = createSelector(
  selectCurrentOrganizationOptions,
  (orgOptions): boolean => Boolean(orgOptions?.[REQUIRE_BUSINESS_REASON_FOR_DIRECT_SENDS]),
);

export const selectIsShipmentBusinessReasonRequired = createSelector(
  selectCsIsBusinessReasonRequiredByCampaign,
  selectIsShipmentBusinessReasonRequiredByOrg,
  (byCampaign, byOrg): boolean => byCampaign || byOrg,
);

export const selectShipmentInfoGate = createSelector(
  selectCsShipmentInfoForm,
  selectCsBusinessReasonId,
  selectIsShipmentBusinessReasonRequired,
  selectIsCsOrgSettingsResolved,
  selectIsCsReleaseDateMissing,
  (
    form,
    businessReasonId,
    isBusinessReasonRequired,
    isOrgSettingsResolved,
    isReleaseDateMissing,
  ): IShipmentInfoGate => {
    const contactComplete = Boolean(form.firstName.trim() && form.lastName.trim() && isValidEmail(form.email));
    const addressComplete = Boolean(
      form.country.trim() &&
        form.address1.trim() &&
        form.city.trim() &&
        (!isStateRequired(form.country) || form.state.trim()) &&
        form.zip.trim(),
    );

    if (!contactComplete) {
      return { valid: false, reason: 'incomplete-contact' };
    }
    if (!addressComplete) {
      return { valid: false, reason: 'incomplete-destination' };
    }
    if (!isValidZip(form.zip)) {
      return { valid: false, reason: 'invalid-zip' };
    }
    if (isUnknownState(form.state, form.country)) {
      return { valid: false, reason: 'invalid-state' };
    }
    if (isBusinessReasonRequired && !businessReasonId) {
      return { valid: false, reason: 'business-reason-required' };
    }
    if (!isOrgSettingsResolved && !businessReasonId) {
      return { valid: false, reason: 'org-settings-unknown' };
    }
    if (isReleaseDateMissing) {
      return { valid: false, reason: 'release-date-required' };
    }
    return { valid: true, reason: null };
  },
);
